// Generic state-machine engine voor approval-workflows (module E5).
//
// Doel: één plek om approval-/state-transitions te definiëren ipv per-module
// hardcoded `if (status === X && newStatus === Y)`-checks. Workflows worden
// opgegeven als `WorkflowDefinition<S>` (states + transitions + optionele
// guard + rolesAllowed) en de `WorkflowEngine<S>` voert de checks uit.
//
// Design-principes:
//   - Pure TypeScript, geen Node.js-built-ins (§A10): mag in browser-bundles.
//   - Geen Prisma- of Fastify-dependencies — engine is isomorf.
//   - Generic over `S extends string` zodat workflow-status-types per module
//     hun eigen string-literal-union behouden (LeaveStatus, DeclaratieStatus,
//     etc.) en TypeScript onverenigbare overgangen als type-fout signaleert
//     bij workflow-definitie-tijd.
//   - Guard returnt `boolean | string`: `true`/`false` voor pass/fail zonder
//     reden, of een string-error-message voor pass-of-fail-met-reden.
//   - `validTransitions(from, role)` filtert op rol als `rolesAllowed` is
//     gezet; ongespecificeerd = altijd toegestaan (alleen state-machine).

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

/**
 * Eén toegestane state-transition.
 *
 * - `from` / `to`: bron- en doelstate uit dezelfde generic union `S`.
 * - `label`: human-readable beschrijving (UI-knop-tekst, audit-tekst).
 * - `guard`: optionele check op `context`. Returnt `true` voor pass,
 *   `false` voor generieke fail, of `string` voor fail-met-reason.
 * - `rolesAllowed`: optionele lijst rollen die deze overgang mogen triggeren;
 *   ongedefinieerd = geen rol-restrictie.
 */
export interface StateTransition<S extends string> {
  from: S
  to: S
  label: string
  guard?: (context: unknown) => boolean | string
  rolesAllowed?: string[]
}

/** Definitie van een workflow: alle states + alle toegestane transitions. */
export interface WorkflowDefinition<S extends string> {
  /** Naam voor logging/error-messages, bv. 'verlof'. */
  name: string
  /** Initiële state — bv. 'CONCEPT'. */
  initial: S
  /** Volledige lijst geldige states. */
  states: S[]
  /** Volledige lijst geldige transitions. */
  transitions: StateTransition<S>[]
}

/** Resultaat van een `canTransition`-check. */
export interface TransitionCheckResult {
  allowed: boolean
  /** Reden van afwijzing — null bij `allowed === true`. */
  reason: string | null
}

// ────────────────────────────────────────────────────────────────────────────
// Engine
// ────────────────────────────────────────────────────────────────────────────

/**
 * Generic state-machine engine.
 *
 * Instantieer met een `WorkflowDefinition<S>`. De engine valideert bij
 * constructie dat:
 *   - `initial` een geldige state is.
 *   - Alle `from` en `to` van transitions geldige states zijn.
 *
 * Bij ongeldige definitie: throw `Error` (faalt fail-fast bij module-load).
 */
export class WorkflowEngine<S extends string> {
  private readonly definition: WorkflowDefinition<S>
  private readonly stateSet: Set<S>

  constructor(definition: WorkflowDefinition<S>) {
    this.definition = definition
    this.stateSet = new Set(definition.states)

    if (!this.stateSet.has(definition.initial)) {
      throw new Error(
        `WorkflowEngine[${definition.name}]: initial state "${definition.initial}" is geen geldige state`,
      )
    }

    for (const t of definition.transitions) {
      if (!this.stateSet.has(t.from)) {
        throw new Error(
          `WorkflowEngine[${definition.name}]: transition.from "${t.from}" is geen geldige state`,
        )
      }
      if (!this.stateSet.has(t.to)) {
        throw new Error(
          `WorkflowEngine[${definition.name}]: transition.to "${t.to}" is geen geldige state`,
        )
      }
    }
  }

  /** Naam van de workflow (uit de definitie). */
  getName(): string {
    return this.definition.name
  }

  /** Initiële state van deze workflow. */
  getInitial(): S {
    return this.definition.initial
  }

  /** Alle states uit de definitie. */
  getStates(): S[] {
    return [...this.definition.states]
  }

  /** True als `s` de initial state is. */
  isInitial(s: S): boolean {
    return s === this.definition.initial
  }

  /**
   * True als state `s` een terminal state is — er bestaan géén transitions
   * meer waar `from === s`. Een terminal state is een eindpunt van de flow.
   */
  isTerminal(s: S): boolean {
    if (!this.stateSet.has(s)) {
      return false
    }
    return !this.definition.transitions.some((t) => t.from === s)
  }

  /**
   * Check of een transitie van `from` naar `to` toegestaan is.
   *
   * Volgorde van checks:
   *   1. State-existence (anders allowed=false met reason).
   *   2. Transition-bestaan in definitie.
   *   3. Guard, indien aanwezig.
   *
   * Rol-check zit NIET in `canTransition` — gebruik `validTransitions(from, role)`
   * om rolafhankelijke filtering te doen vóór de canTransition-check, of
   * roep `canTransitionForRole(...)` aan.
   */
  canTransition(from: S, to: S, context?: unknown): TransitionCheckResult {
    if (!this.stateSet.has(from)) {
      return { allowed: false, reason: `Onbekende state: ${from}` }
    }
    if (!this.stateSet.has(to)) {
      return { allowed: false, reason: `Onbekende doelstate: ${to}` }
    }

    const transition = this.definition.transitions.find(
      (t) => t.from === from && t.to === to,
    )
    if (!transition) {
      return {
        allowed: false,
        reason: `Overgang ${from} → ${to} niet toegestaan`,
      }
    }

    if (transition.guard) {
      const guardResult = transition.guard(context)
      if (guardResult === false) {
        return { allowed: false, reason: `Guard faalt voor ${from} → ${to}` }
      }
      if (typeof guardResult === 'string') {
        return { allowed: false, reason: guardResult }
      }
    }

    return { allowed: true, reason: null }
  }

  /**
   * Variant van `canTransition` met expliciete rol-check.
   *
   * Als de transitie een `rolesAllowed`-lijst heeft, moet `userRole` daarin
   * voorkomen — anders allowed=false met "Rol ... niet toegestaan"-reason.
   */
  canTransitionForRole(
    from: S,
    to: S,
    userRole: string | null | undefined,
    context?: unknown,
  ): TransitionCheckResult {
    const base = this.canTransition(from, to, context)
    if (!base.allowed) return base

    const transition = this.definition.transitions.find(
      (t) => t.from === from && t.to === to,
    )
    if (transition?.rolesAllowed && transition.rolesAllowed.length > 0) {
      if (!userRole || !transition.rolesAllowed.includes(userRole)) {
        return {
          allowed: false,
          reason: `Rol "${userRole ?? 'onbekend'}" niet toegestaan voor ${from} → ${to}`,
        }
      }
    }

    return { allowed: true, reason: null }
  }

  /**
   * Lijst alle transities die vanaf `from` mogelijk zijn.
   *
   * Als `userRole` is meegegeven, filter op transities waar `rolesAllowed`
   * ofwel ongedefinieerd is (geen restrictie) ofwel `userRole` bevat.
   *
   * Geen guard-evaluatie: dit is een statische lijst, niet runtime-context-
   * afhankelijk. Gebruik `canTransition(...)` voor de runtime-check inclusief
   * guard.
   */
  validTransitions(from: S, userRole?: string | null): StateTransition<S>[] {
    if (!this.stateSet.has(from)) return []
    return this.definition.transitions.filter((t) => {
      if (t.from !== from) return false
      if (!t.rolesAllowed || t.rolesAllowed.length === 0) return true
      if (!userRole) return false
      return t.rolesAllowed.includes(userRole)
    })
  }

  /**
   * Lijst alle target-states die vanaf `from` bereikbaar zijn (optioneel
   * filterend op rol via `userRole`).
   *
   * Convenience-helper bovenop `validTransitions` voor UI-knopjes-rendering.
   */
  validTargets(from: S, userRole?: string | null): S[] {
    return this.validTransitions(from, userRole).map((t) => t.to)
  }
}
